import React, { useEffect, useState } from "react";
import ReactFlow, { useReactFlow, ReactFlowProvider } from "reactflow";
import "reactflow/dist/style.css";
import { useNavigate } from "react-router-dom";
import { Handle, Position } from "reactflow";
import { stage } from "../../assets/globalHelpers";
import "../../assets/css/diagramms/graphDiagram.css"
import { stagesNames } from "../translation.js"
import "../../assets/css/argon-dashboard-react.css"
import "../../assets/css/colors.css"
import { useLanguage } from "context/LanguageProvider";
import { useDarkMode } from "context/DarkModeProvider";
// ❌ ΑΦΑΙΡΕΣΕ: import axiosInstance from "apiHandles/axiosInstance";

function ButtonNode({ data }) {
  const { structureType, darkMode } = data;
  const sourcePosition = structureType === "List" ? Position.Right : Position.Bottom;
  const targetPosition = structureType === "List" ? Position.Left : Position.Top;

  const { selLanguage, toggleLanguage, isLoading } = useLanguage();

  const colorSchemes = {
    HashTable: { primary: '#10b981', secondary: '#059669', accent: '#d1fae5' },
    Tree: { primary: '#3b82f6', secondary: '#2563eb', accent: '#dbeafe' },
    List: { primary: '#8b5cf6', secondary: '#7c3aed', accent: '#ede9fe' },
    Stack: { primary: '#f59e0b', secondary: '#d97706', accent: '#fef3c7' },
    Queue: { primary: '#ef4444', secondary: '#dc2626', accent: '#fecaca' },
    Graph: { primary: '#06b6d4', secondary: '#0891b2', accent: '#cffafe' },
    Array: { primary: '#ec4899', secondary: '#db2777', accent: '#fce7f3' },
    Set: { primary: '#84cc16', secondary: '#65a30d', accent: '#ecfccb' },
  };

  const scheme = colorSchemes[structureType] || colorSchemes.Graph;

  return (
    <div className="graphDiagramContainer">
      <button
        className="graphDiagramButton"
        onClick={(event) => {
          event.stopPropagation();
          data.onClick();
        }}
        onMouseOver={(e) => {
          e.currentTarget.style.background = `linear-gradient(135deg, ${scheme.primary}25 0%, ${scheme.secondary}20 100%)`;
          e.currentTarget.style.transform = "scale(1.05) translateY(-4px)";
          e.currentTarget.style.boxShadow = `0 0px 10px ${scheme.primary}40, 0 0 0 1px ${scheme.primary}20`;
        }}
        onMouseOut={(e) => {
          e.currentTarget.style.background = `linear-gradient(135deg, ${scheme.primary}15 0%, ${scheme.secondary}10 100%)`;
          e.currentTarget.style.transform = "scale(1)";
          e.currentTarget.style.boxShadow = `none`;
        }}
        style={{
          background: `linear-gradient(135deg, ${scheme.primary}15 0%, ${scheme.secondary}10 100%)`,
          backdropFilter: 'blur(10px)',
          border: `1px solid ${scheme.primary}30`,
          borderRadius: "12px",
          // boxShadow: `0 4px 8px ${scheme.primary}20`,
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          cursor: 'pointer',
          overflow: 'hidden',
          position: 'relative',
          fontFamily: '"Inter", "Poppins", sans-serif',
          fontSize: "14px",
          fontWeight: "600",
        }}
      >
        <div
          style={{
            position: 'absolute',
            inset: '0',
            background: `linear-gradient(135deg, ${scheme.primary}15 0%, ${scheme.secondary}10 100%)`,
            transition: 'all 0.3s ease',
            borderRadius: '12px',
          }}
        />

        <div
          style={{
            position: 'relative',
            zIndex: 1,
            color: darkMode ? "var(--commonWhite)" : scheme.primary,
            background: `linear-gradient(135deg, ${scheme.primary} 0%, ${scheme.secondary} 100%)`,
            WebkitBackgroundClip: 'text',
            backgroundClip: 'text',
            textAlign: 'center',
          }}
        >
          {selLanguage ? stagesNames[data.label.toLowerCase()] : data.label}
        </div>
      </button>

      <Handle
        type="target"
        position={targetPosition}
        style={{ opacity: 0, pointerEvents: "none", zIndex: 1 }}
        isConnectable={false}
      />
      <Handle
        type="source"
        position={sourcePosition}
        style={{ opacity: 0, pointerEvents: "none", zIndex: 1 }}
        isConnectable={false}
      />
    </div>
  );
}

const nodeTypes = {
  buttonNode: ButtonNode,
};

// Ξεχωριστό component για το ReactFlow content
function GraphDiagramContent({ nodes, edges, structureType, finalStages, darkMode }) {
  const navigate = useNavigate();
  const { fitView } = useReactFlow();

  const customNodes = nodes.map((node) => ({
    ...node,
    type: "buttonNode",
    data: {
      ...node.data,
      structureType,
      darkMode,
      onClick: () => {
        const stageId = node.data.stageId;
        const stageName = node.data.label;


        if (!stageId) {
          console.error("No stage ID found");
          return;
        }


        localStorage.setItem("stageLocation", stageName);
        localStorage.setItem("selectedStageId", stageId);

        navigate("/admin/index");
      }
    },
  }));

  useEffect(() => {
    const handleResize = () => {
      requestAnimationFrame(() => {
        fitView({ padding: 0.2, duration: 100 });
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [fitView]);

  useEffect(() => {
    requestAnimationFrame(() => {
      fitView({ padding: 0.2, duration: 100 });
    });
  }, [nodes, edges, fitView]);

  return (
    <ReactFlow
      nodes={customNodes}
      edges={edges}
      nodeTypes={nodeTypes}
      fitView
      panOnDrag={false}
      zoomOnScroll={false}
      zoomOnPinch={false}
      zoomOnDoubleClick={false}
      nodesDraggable={false}
      nodesConnectable={false}
      elementsSelectable={false}
      edgesFocusable={false}
      edgesUpdatable={false}
      fitViewOptions={{ padding: 0.2 }}
      proOptions={{ hideAttribution: true }}
      style={{ background: darkMode ? "#0f172a" : "var(--lightModeBack)" }}
    />
  );
}

// Κύριο component που διαχειρίζεται το darkMode state
function GraphDiagram({ nodes, edges, structureType, finalStages }) {
  const { darkMode } = useDarkMode();

  return (
    <div className="graphSecondContainer">
      <ReactFlowProvider>
        <GraphDiagramContent
          nodes={nodes}
          edges={edges}
          structureType={structureType}
          finalStages={finalStages}
          darkMode={darkMode}
        />
      </ReactFlowProvider>
    </div>
  );
}

export default GraphDiagram;